import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * @typedef {Object} DeliveryMetaPayload
 * @property {number} lead
 * @property {number} transit
 * @property {string} method
 * @property {string} status
 * @property {boolean} fast
 * @property {string} shipping
 * @property {string} delivery
 */

/**
 * @param {Date} endDate
 * @returns {string}
 */
function formatDeliveryRange(endDate) {
  return window.NoomaBusinessDays.formatDeliveryRange(endDate, MONTHS);
}

/**
 * @param {string | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function parseDayCount(value, fallback) {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * @param {ParentNode} root
 * @returns {{
 *   deliveryEntries: Record<string, string>[],
 *   shippingEntries: Record<string, string>[],
 *   metaEntries: Record<string, DeliveryMetaPayload>[],
 *   latestDate: Date | null,
 *   allFast: boolean,
 *   hasMarkers: boolean
 * }}
 */
function collectDeliveryMarkers(root) {
  /** @type {Map<string, string>} */
  const deliveryBySku = new Map();
  /** @type {Map<string, string>} */
  const shippingBySku = new Map();
  /** @type {Map<string, DeliveryMetaPayload>} */
  const metaBySku = new Map();
  let allFast = true;
  let hasMarkers = false;

  for (const marker of root.querySelectorAll('[data-cart-delivery-marker]')) {
    if (!(marker instanceof HTMLElement)) continue;

    const sku = marker.dataset.cartDeliverySku?.trim();
    const deliveryDate = marker.dataset.cartDeliveryDate?.trim();
    const shippingDate = marker.dataset.cartShippingDate?.trim() ?? '';
    const status = marker.dataset.cartDeliveryStatus?.trim() ?? '';
    const method = marker.dataset.cartDeliveryMethod?.trim() || 'courier';
    const fast = marker.dataset.cartDeliveryFast === 'true';
    const lead = parseDayCount(marker.dataset.cartDeliveryLead, 2);
    const transit = parseDayCount(marker.dataset.cartDeliveryTransit, 2);

    if (!sku || !deliveryDate) continue;

    hasMarkers = true;

    const existingDelivery = deliveryBySku.get(sku);
    if (!existingDelivery || deliveryDate > existingDelivery) {
      deliveryBySku.set(sku, deliveryDate);
    }

    if (shippingDate) {
      const existingShipping = shippingBySku.get(sku);
      if (!existingShipping || shippingDate > existingShipping) {
        shippingBySku.set(sku, shippingDate);
      }
    }

    const existingMeta = metaBySku.get(sku);
    if (!existingMeta || deliveryDate > existingMeta.delivery) {
      metaBySku.set(sku, {
        lead,
        transit,
        method,
        status,
        fast,
        shipping: shippingDate,
        delivery: deliveryDate,
      });
    } else if (shippingDate && shippingDate > existingMeta.shipping) {
      metaBySku.set(sku, {
        ...existingMeta,
        shipping: shippingDate,
      });
    }

    if (!fast) {
      allFast = false;
    }
  }

  // Align meta shipping/delivery with independently aggregated max dates per SKU.
  for (const [sku, meta] of metaBySku) {
    const maxDelivery = deliveryBySku.get(sku);
    const maxShipping = shippingBySku.get(sku);
    if (maxDelivery) meta.delivery = maxDelivery;
    if (maxShipping) meta.shipping = maxShipping;
  }

  const deliveryEntries = Array.from(deliveryBySku.entries()).map(([sku, date]) => ({ [sku]: date }));
  const shippingEntries = Array.from(shippingBySku.entries()).map(([sku, date]) => ({ [sku]: date }));
  const metaEntries = Array.from(metaBySku.entries()).map(([sku, meta]) => ({ [sku]: meta }));

  const latestDate =
    deliveryBySku.size > 0
      ? new Date(
          Math.max(
            ...Array.from(deliveryBySku.values()).map((dateText) =>
              new Date(`${dateText}T12:00:00`).getTime()
            )
          )
        )
      : null;

  return { deliveryEntries, shippingEntries, metaEntries, latestDate, allFast, hasMarkers };
}

/**
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * @returns {{ fbp: string, fbc: string }}
 */
function getMetaTrackingAttributes() {
  return {
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
  };
}

/** @type {Promise<void> | null} */
let pendingAttributeUpdate = null;

/**
 * Persists delivery/shipping dates, delivery_meta, and Meta tracking cookies on cart.attributes.
 *
 * @param {Record<string, string>[]} [deliveryEntries]
 * @param {Record<string, string>[]} [shippingEntries]
 * @param {Record<string, DeliveryMetaPayload>[]} [metaEntries]
 */
async function persistCartAttributes(deliveryEntries = [], shippingEntries = [], metaEntries = []) {
  if (pendingAttributeUpdate) {
    await pendingAttributeUpdate;
  }

  pendingAttributeUpdate = (async () => {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const attributes = { ...(cart.attributes ?? {}) };
      const nextDelivery = deliveryEntries.length > 0 ? JSON.stringify(deliveryEntries) : '';
      const nextShipping = shippingEntries.length > 0 ? JSON.stringify(shippingEntries) : '';
      const nextDeliveryMeta = metaEntries.length > 0 ? JSON.stringify(metaEntries) : '';
      const { fbp, fbc } = getMetaTrackingAttributes();

      const hasChanges =
        attributes.delivery !== nextDelivery ||
        attributes.shipping !== nextShipping ||
        attributes.delivery_meta !== nextDeliveryMeta ||
        (attributes.fbp ?? '') !== fbp ||
        (attributes.fbc ?? '') !== fbc;

      if (!hasChanges) return;

      attributes.delivery = nextDelivery;
      attributes.shipping = nextShipping;
      attributes.delivery_meta = nextDeliveryMeta;
      attributes.fbp = fbp;
      attributes.fbc = fbc;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (error) {
      console.error('dev-cart-delivery: failed to update cart attributes', error);
    } finally {
      pendingAttributeUpdate = null;
    }
  })();

  await pendingAttributeUpdate;
}

/** Meta cookie sync when cart summary (and dev-cart-delivery) is not in DOM yet — e.g. empty cart. */
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event instanceof CustomEvent && event.detail?.data?.itemCount === 0) {
    requestAnimationFrame(() => {
      persistCartAttributes([], [], []);
    });
    return;
  }

  if (document.querySelector('dev-cart-delivery')) return;

  requestAnimationFrame(() => {
    persistCartAttributes([], [], []);
  });
});

/**
 * Clears delivery/shipping/delivery_meta from cart.attributes while preserving Meta cookies.
 */
export async function clearCartDeliveryAttributes() {
  await persistCartAttributes([], [], []);
}

/**
 * @param {Record<string, string>[]} entries
 * @returns {Record<string, string>[]}
 */
function mergeDateEntries(entries) {
  /** @type {Map<string, string>} */
  const bySku = new Map();

  for (const entry of entries) {
    const sku = Object.keys(entry)[0];
    const date = entry[sku];
    if (!sku || !date) continue;

    const existing = bySku.get(sku);
    if (!existing || date > existing) {
      bySku.set(sku, date);
    }
  }

  return Array.from(bySku.entries()).map(([sku, date]) => ({ [sku]: date }));
}

/**
 * @param {Record<string, DeliveryMetaPayload>[]} entries
 * @returns {Record<string, DeliveryMetaPayload>[]}
 */
function mergeMetaEntries(entries) {
  /** @type {Map<string, DeliveryMetaPayload>} */
  const bySku = new Map();

  for (const entry of entries) {
    const sku = Object.keys(entry)[0];
    const meta = entry[sku];
    if (!sku || !meta?.delivery) continue;

    const existing = bySku.get(sku);
    if (!existing || meta.delivery > existing.delivery) {
      bySku.set(sku, { ...meta });
      continue;
    }

    if (meta.shipping && meta.shipping > existing.shipping) {
      bySku.set(sku, {
        ...existing,
        shipping: meta.shipping,
      });
    }
  }

  return Array.from(bySku.entries()).map(([sku, meta]) => ({ [sku]: meta }));
}

/**
 * Aggregates per-line delivery markers and updates footer copy + cart.attributes.
 *
 * @param {ParentNode} [root]
 */
export async function refreshCartDelivery(root = document) {
  const components = root.querySelectorAll('dev-cart-delivery');
  if (components.length === 0) return;

  /** @type {Record<string, string>[]} */
  const combinedDelivery = [];
  /** @type {Record<string, string>[]} */
  const combinedShipping = [];
  /** @type {Record<string, DeliveryMetaPayload>[]} */
  const combinedMeta = [];

  for (const element of components) {
    if (!(element instanceof DevCartDelivery)) continue;
    const { deliveryEntries, shippingEntries, metaEntries } = element.syncFromMarkers();
    combinedDelivery.push(...deliveryEntries);
    combinedShipping.push(...shippingEntries);
    combinedMeta.push(...metaEntries);
  }

  await persistCartAttributes(
    mergeDateEntries(combinedDelivery),
    mergeDateEntries(combinedShipping),
    mergeMetaEntries(combinedMeta)
  );
}

/**
 * Cart drawer + cart page delivery summary under estimated total.
 *
 * @extends {Component<{ label: HTMLElement, date: HTMLElement, check: HTMLElement }>}
 */
class DevCartDelivery extends Component {
  /** @type {(() => void) | null} */
  #scheduleRefresh = null;

  /** @type {((event: Event) => void) | null} */
  #onSectionHydrated = null;

  connectedCallback() {
    super.connectedCallback();

    this.#scheduleRefresh = () => {
      // Double rAF: run after cart section morph completes (avoids touching Horizon morph.js).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          refreshCartDelivery(document);
        });
      });
    };

    this.#onSectionHydrated = (event) => {
      if (!(event instanceof CustomEvent)) return;
      if (!event.target?.contains?.(this)) return;

      this.#scheduleRefresh?.();
    };

    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleRefresh);
    document.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.addEventListener('section:hydrated', this.#onSectionHydrated);

    this.#scheduleRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scheduleRefresh) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleRefresh);
    }

    if (this.#onSectionHydrated) {
      document.removeEventListener('section:hydrated', this.#onSectionHydrated);
    }

    document.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRefresh?.();
  }

  #onDialogOpen = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLElement)) return;
    if (!dialog.closest('cart-drawer-component')) return;

    this.#scheduleRefresh?.();
  };

  /**
   * Reads markers from the nearest cart-items-component and updates this instance.
   *
   * @returns {{
   *   deliveryEntries: Record<string, string>[],
   *   shippingEntries: Record<string, string>[],
   *   metaEntries: Record<string, DeliveryMetaPayload>[]
   * }}
   */
  syncFromMarkers() {
    const markersRoot = this.closest('cart-items-component') ?? document;
    const { deliveryEntries, shippingEntries, metaEntries, latestDate, allFast, hasMarkers } =
      collectDeliveryMarkers(markersRoot);
    this.updateDisplay(latestDate, allFast, hasMarkers);
    return { deliveryEntries, shippingEntries, metaEntries };
  }

  /**
   * @param {Date | null} latestDate
   * @param {boolean} allFast
   * @param {boolean} hasMarkers
   */
  updateDisplay(latestDate, allFast, hasMarkers) {
    const { label, date } = this.refs;
    if (!(date instanceof HTMLElement)) return;

    const ownedLang = window.NoomaDeliveryI18n?.currentLang?.();

    if (hasMarkers && latestDate) {
      this.hidden = false;
      this.dataset.deliveryEnd = latestDate.toISOString().slice(0, 10);
    } else if (!date.textContent.trim() && !this.dataset.deliveryEnd) {
      this.hidden = true;
      return;
    } else {
      this.hidden = false;
    }

    this.classList.add('cart-delivery-info--ready');

    // Morph injects EN Liquid before line markers exist — paint from data-delivery-end instead.
    if (ownedLang) {
      window.NoomaDeliveryI18n?.paint(this);
      return;
    }

    if (!hasMarkers || !latestDate) return;

    if (label instanceof HTMLElement) {
      label.textContent =
        window.Theme?.translations?.cart_delivered_between_label ?? 'Delivered between ';
    }
    date.textContent = formatDeliveryRange(latestDate);
  }
}

if (!customElements.get('dev-cart-delivery')) {
  customElements.define('dev-cart-delivery', DevCartDelivery);
}
